import { Flex, HStack, Stack, Text } from "@chakra-ui/react";
import { useRef, useState } from "react";
import type { CustomHoliday } from "@/api/CustomHoliday";
import type { Organization } from "@/api/Organization";
import type { User } from "@/api/User";
import SectionPanel from "@/components/SectionPanel";
import { Button } from "@/components/ui/button";
import { Alert } from "@/components/ui/alert";
import { useI18n } from "@/i18n/I18nContext";
import { CustomHolidayDialog, type HolidayAction } from "./CustomHolidayDialog";
import { HolidayRuleDetails } from "./HolidayDetails";

export const CustomHolidayAdmin = ({ rules, loading, failed, organization, actor, onChanged }: {
  rules: CustomHoliday[];
  loading: boolean;
  failed: boolean;
  organization: Organization;
  actor: User;
  onChanged: () => void;
}) => {
  const { t } = useI18n();
  const [dialog, setDialog] = useState<{ action: HolidayAction; rule?: CustomHoliday } | null>(null);
  const addButton = useRef<HTMLButtonElement>(null);
  const today = new Date().toISOString().slice(0, 10);
  return <>
    <SectionPanel title={t("customHolidays.title", { organization: organization.name })} actions={
      <Button ref={addButton} colorPalette="blue" onClick={() => setDialog({ action: "create" })}>{t("customHolidays.add")}</Button>
    }>
      {loading ? <Text role="status">{t("publicHolidays.loading")}</Text>
        : failed ? <Alert role="alert" status="error" title={t("publicHolidays.rulesError")} />
        : !rules.length ? <Text color="fg.muted">{t("customHolidays.empty")}</Text>
        : <Stack gap="3">{rules.map(rule => {
          const started = rule.localValidFrom <= today;
          const readOnly = (started && rule.dateRule.type === "once") || (!!rule.localValidUntil && rule.localValidUntil <= today);
          return <Flex as="article" key={rule.id} aria-label={rule.name} gap="3" justify="space-between" wrap="wrap" borderBottomWidth="1px" borderColor="border" pb="3">
            <Stack gap="1" minW="0" flex="1">
              <Text fontWeight="semibold" overflowWrap="anywhere">{rule.name}</Text>
              <Text fontSize="xs">{t(rule.lengthInDays === 0.5 ? "publicHolidays.halfDay" : "publicHolidays.fullDay")}</Text>
              <HolidayRuleDetails rule={rule} />
              {readOnly && <Text fontSize="xs" color="fg.muted">{t("customHolidays.readOnly")}</Text>}
            </Stack>
            {!readOnly && <HStack gap="2">
              <Button size="sm" variant="outline" aria-label={t("customHolidays.editNamed", { name: rule.name })}
                onClick={() => setDialog({ action: "edit", rule })}>{t("customHolidays.edit")}</Button>
              <Button size="sm" variant="outline" colorPalette="red" aria-label={t("customHolidays.removeNamed", { name: rule.name })}
                onClick={() => setDialog({ action: "remove", rule })}>{t(started ? "customHolidays.end" : "customHolidays.remove")}</Button>
            </HStack>}
          </Flex>;
        })}</Stack>}
    </SectionPanel>
    {dialog && <CustomHolidayDialog {...dialog} actor={actor} onSaved={onChanged}
      finalFocusEl={() => addButton.current} onClose={() => setDialog(null)} />}
  </>;
};
